/**
 * Shared Test Email Dialog Component
 * Used by both webinar-mailings and plx-mailings
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

export interface TestEmailClient {
  code: string;
  name: string;
  esp_type: 'iterable' | 'mailchimp';
}

export interface TestEmailResult {
  success: boolean;
  message?: string;
}

export interface TestEmailDialogProps {
  open: boolean;
  onClose: () => void;
  /** Callback when user clicks send. Returns success/message. */
  onSend: (emails: string[], clientCodes: string[]) => Promise<TestEmailResult>;
  /** List of clients available for testing (filtered to email's selected_clients) */
  availableClients: TestEmailClient[];
  /** Default clients to pre-select when dialog opens */
  defaultSelectedClients?: string[];
  title?: string;
  /** Subtitle text shown below title */
  subtitle?: string;
}

const TestEmailDialog: React.FC<TestEmailDialogProps> = ({
  open,
  onClose,
  onSend,
  availableClients,
  defaultSelectedClients = [],
  title = 'Test-E-Mail senden',
  subtitle,
}) => {
  const [testEmails, setTestEmails] = useState('');
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens. Filter the default against availableClients
  // so we never carry hidden state for codes that have no checkbox — the user
  // can't uncheck what doesn't render, and otherwise those codes leak into the
  // POST body. See incident 2026-04-30 for the PLX repro.
  useEffect(() => {
    if (open) {
      const availableCodes = new Set(availableClients.map((c) => c.code));
      setSelectedClients(defaultSelectedClients.filter((code) => availableCodes.has(code)));
      setError(null);
    }
  }, [open, defaultSelectedClients, availableClients]);

  const handleSend = async () => {
    const emailList = testEmails
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (emailList.length === 0) {
      setError('Bitte mindestens eine E-Mail-Adresse eingeben');
      return;
    }

    if (selectedClients.length === 0) {
      setError('Bitte mindestens einen Client auswählen');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const result = await onSend(emailList, selectedClients);
      if (result.success) {
        onClose();
        setTestEmails('');
        setSelectedClients([]);
      } else {
        setError(result.message || 'Fehler beim Senden');
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const toggleClient = (code: string) => {
    setSelectedClients((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SendIcon color="secondary" />
          {title}
        </Box>
      </DialogTitle>
      <DialogContent>
        {subtitle && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {subtitle}
          </Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="E-Mail-Adressen"
              value={testEmails}
              onChange={(e) => setTestEmails(e.target.value)}
              placeholder="email1@example.de, email2@example.de"
              helperText="Mehrere E-Mail-Adressen mit Komma trennen"
              error={!!error && !testEmails.trim()}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Clients für Test auswählen:
          </Typography>
          {availableClients.length === 0 ? (
            <Typography variant="body2" color="error">
              Keine Clients verfügbar. Bitte wählen Sie Clients im Editor.
            </Typography>
          ) : (
            <FormGroup>
              {availableClients.map((client) => (
                <FormControlLabel
                  key={client.code}
                  control={
                    <Checkbox
                      checked={selectedClients.includes(client.code)}
                      onChange={() => toggleClient(client.code)}
                      sx={{
                        color: client.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                        '&.Mui-checked': {
                          color: client.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                        },
                      }}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      {client.name}
                      <Chip
                        size="small"
                        label={client.esp_type === 'iterable' ? 'Iterable' : 'Mailchimp'}
                        sx={{
                          backgroundColor: client.esp_type === 'iterable' ? '#6366f1' : '#ec4899',
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          )}
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSend}
          disabled={sending || !testEmails.trim() || selectedClients.length === 0}
          startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {sending ? 'Sende...' : 'Test senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestEmailDialog;
